/* sb-motion.js — the v3 design surface's motion engine (ADR 0630/0631, S3).
 *
 * PROGRESSIVE ENHANCEMENT CONTRACT (load-bearing): with no JS, no
 * IntersectionObserver, reduced-motion, or motion_intensity 'none', every
 * element is FULLY VISIBLE — the pre-reveal hidden state only applies under
 * `html.sb-motion-on` (added HERE, only when motion is allowed), so critique
 * screenshots and no-JS users never see blank sections.
 *
 * Dependency-free by design: the stripped `pdp` layout does not load Shrine's
 * JS bundles, so this file must never assume jQuery/pubsub/etc.
 *
 * Gates (in order):
 *   1. prefers-reduced-motion: reduce  → no motion, ever.
 *   2. --v3-motion keyword ('none' | 'subtle' | 'full') read from the first
 *      `.pdp-v3` element (emitted per product by sb-pdp-v3-skin-{slug}.css).
 *      'none'   → no motion; 'subtle' → scroll reveals + count-ups only
 *      (CSS marquees pause via the missing `sb-marquee-on` class);
 *      'full'   → everything.
 *
 * Behaviours (fidelity source: docs/design-sources/atlas-bolt-teardown/specs/_motion-system.md):
 *   - Scroll reveal: [data-sb-reveal] fades+rises when it enters the viewport
 *     (0.4s ease-out, 20px rise — the reference theme's own reveal rule, which
 *     its demo never fired due to a class-spelling bug; we implement the intent
 *     correctly). One-shot; children stagger via [data-sb-reveal-child].
 *   - Count-up: [data-sb-countup data-sb-target="92" data-sb-suffix="%"] counts
 *     0 → target over 1800ms ease-out cubic when it enters the viewport
 *     (threshold 0.3), 150ms stagger between siblings — the reference stat-trio
 *     behaviour. The final value is server-rendered; JS only animates TOWARD it.
 */
(function () {
  'use strict';

  function motionLevel() {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'none';
    } catch (e) {
      /* matchMedia unavailable — treat as motion-capable */
    }
    var scope = document.querySelector('.pdp-v3') || document.body;
    var level = '';
    try {
      level = getComputedStyle(scope).getPropertyValue('--v3-motion').trim();
    } catch (e) {
      /* no computed style — fall through to default */
    }
    if (level === 'none' || level === 'subtle' || level === 'full') return level;
    return 'full';
  }

  function initReveals() {
    var nodes = document.querySelectorAll('[data-sb-reveal]');
    if (nodes.length === 0) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          el.classList.add('sb-revealed');
          var children = el.querySelectorAll('[data-sb-reveal-child]');
          for (var i = 0; i < children.length; i++) {
            children[i].style.transitionDelay = i * 50 + 'ms';
            children[i].classList.add('sb-revealed');
          }
          io.unobserve(el);
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px 50px 0px' },
    );
    nodes.forEach(function (el) {
      io.observe(el);
    });
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function animateCount(el, delayMs) {
    var target = parseFloat(el.getAttribute('data-sb-target') || el.textContent || '0');
    if (!isFinite(target)) return;
    var suffix = el.getAttribute('data-sb-suffix') || '';
    var duration = 1800;
    var startTime = null;
    function frame(now) {
      if (startTime === null) startTime = now;
      var elapsed = now - startTime;
      if (elapsed < delayMs) {
        requestAnimationFrame(frame);
        return;
      }
      var t = Math.min(1, (elapsed - delayMs) / duration);
      el.textContent = Math.round(target * easeOutCubic(t)) + suffix;
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function initCountUps() {
    var nodes = document.querySelectorAll('[data-sb-countup]');
    if (nodes.length === 0) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var group = entry.target;
          var counters = group.hasAttribute('data-sb-countup')
            ? [group]
            : [].slice.call(group.querySelectorAll('[data-sb-countup]'));
          counters.forEach(function (el, i) {
            animateCount(el, i * 150);
          });
          io.unobserve(group);
        });
      },
      { threshold: 0.3 },
    );
    nodes.forEach(function (el) {
      io.observe(el);
    });
  }

  function boot() {
    var level = motionLevel();
    if (level === 'none') return; // content is already fully visible
    if (typeof IntersectionObserver === 'undefined') return; // old browser: stay static
    // Only NOW may the pre-reveal hidden state apply (CSS gates on this class).
    document.documentElement.classList.add('sb-motion-on');
    if (level === 'full') document.documentElement.classList.add('sb-marquee-on');
    initReveals();
    initCountUps();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
